// npm
import store from '@/store'
import moment from 'moment'
import { cloneDeep, sortBy } from 'lodash'
import i18n from '@/lang'
// utils
import { formatedDate, getDiffInDays } from '@/utils/date'
import { createSearchString } from '@/utils/search'
import { getCrewMember, getCrewSize } from '@/utils/crew'
import isEqual from '@/utils/isEqual'
import { checkChanges, getFilteredHistory } from '@/utils/flightHistory'
import { getFormattedName, getFullName } from '@/utils/getName'
import getLocalizedName from '@/utils/getLocalizedName'

export default class Briefing {
  #airports = store.getters.dictionaryAirports

  static assign (item) {
    const currentVersion = item.versions[0] // function to sort by date will be here
    const briefing = Object.assign(new Briefing(), {
      currentVersion,
      hasFilesInSections: Object.values(currentVersion.sections).some(item => {
        return item.files.filesNum > 0 && store.state.sectionTypes.availableCodes.includes(item.code)
      }),
      status: currentVersion.status,
      id: item.id,
      actuality: item.actuality,
      etd: item.etd,
      displayEtd: moment(item.etd).format('HH:mm, DD.MM.YY'),
      displayDateEtd: moment(item.etd).format('YYYY-MM-DD'),
      displayTimeEtd: moment(item.etd).format('HH:mm'),
      etdHint: '',
      std: item.std,
      displayStd: moment(item.std).format('HH:mm, DD.MM.YY'),
      displayDateStd: moment(item.std).format('YYYY-MM-DD'),
      displayTimeStd: moment(item.std).format('HH:mm'),
      readyTime: moment(item.etd).subtract(store.getters.systemSettings.BRIEFING_READY_TIMEOUT, 'minutes').format('HH:mm'),
      flightNumber: item.flightNum,
      tailNumber: item.tail,
      acType: item.acType,
      depPort: item.depPort,
      depPortIata: null,
      arrPort: item.arrPort,
      arrPortIata: null,
      notes: item.notes || '',
      versions: item.versions,
      flightHistory: item.flightHistory ? sortBy(item.flightHistory, el => -el.id) : [],
      wasApproved: item.wasApproved,
      isApprovedAndChanged: currentVersion.status === 'CH' && !!currentVersion.approveDate,
      isStdAndEtdOnDifferentDays: !!getDiffInDays(item.std, item.etd),
      approveDate: currentVersion.approveDate
        ? moment(currentVersion.approveDate).format('HH:mm')
        : '',
      displayApproveDate: formatedDate(currentVersion.approveDate, 'HH:mm, DD.MM.YY'),
      approveDateDiff: getDiffInDays(currentVersion.approveDate),
      cancelled: item.cancelled,
      crew: getSortedCrew(item.crew),
      crewSize: getCrewSize(item.crew),
      crewActivity: item.crewActivity || [],
      navlogItems: currentVersion.navlogItems || [],
      numOfPilots: item.crew.PILOT?.length || 0,
      numOfStewards: item.crew.STEWARD?.length || 0,
      hScrollHint: '',
      deskDisplayName: store.getters.deskNameById(item.deskId),
      deskId: item.deskId
    })

    if (import.meta.env.DEV) {
      briefing._src = cloneDeep(item)
    }

    briefing.depPortIata = briefing.getIataCode(briefing.depPort)
    briefing.arrPortIata = briefing.getIataCode(briefing.arrPort)
    briefing.route = getRoute(item, briefing.depPortIata, briefing.arrPortIata)
    briefing.hScrollHint = `${briefing.flightNumber} ${briefing.route} <br/> ${formatedDate(briefing.etd, 'DD / HH:mm')}`
    briefing.etdHint = briefing.isStdAndEtdOnDifferentDays
      ? `STD ${briefing.displayStd} <br/> ETD ${briefing.displayEtd}`
      : `STD ${briefing.displayTimeStd} / ETD ${briefing.displayTimeEtd}`

    function getRoute ({ depPort, arrPort }, depPortIata, arrPortIata) {
      const fallback = i18n.global.t('not-available')
      const dep = store.getters.iataMode ? depPortIata : depPort
      const arr = store.getters.iataMode ? arrPortIata : arrPort

      return `${dep || fallback}–${arr || fallback}`
    }

    function getSortedCrew (crew) {
      const sortedCrewByOrderNum = {}
      if (crew) {
        Object.entries(crew).forEach(([key, value]) => {
          sortedCrewByOrderNum[key] = sortBy(value, item => item.orderNum)
        })
      }
      return sortedCrewByOrderNum
    }

    briefing.recalcHistoryFields()

    if (briefing.crewActivity.length) {
      briefing.prepareCrewActivityData()
    }

    briefing.navlogItems.forEach(item =>
        item.status = item.processingResult
            ? (item.processingResult === 'OK' ? 'OK' : 'ERROR')
            : 'WAIT')

    if (currentVersion.approveUserId) {
      briefing.currentVersion.fdApprover = store.getters.userById(currentVersion.approveUserId)
    }

    briefing.searchString = createSearchString(
      briefing,
      'flightNumber',
      (briefing) => (formatedDate(briefing.etd, 'DD-MM-YYYY')),
      'route',
      'tailNumber'
    )

    return briefing
  }

  prepareCrewActivityData () {
    const presentedCrewIds = []
    for (const item of this.crewActivity) {
      const person = getCrewMember(this.crew, item.id)
      presentedCrewIds.push(item.id)

      if (!person) {
        continue
      }

      item.duty = getLocalizedName(person.duty),
      item.name = {
        formatted: getFormattedName(person),
        fullName: getFullName(person)
      }
      item.phone = person.contacts?.phone || []
      item.navlogItem = this.navlogItems.filter(i => i.crewId == item.id)[0]
    }

    // create pseudo activity items for navlog
    const pseudoCrewIds = []
    for (const navlog of this.navlogItems.filter(navlog => !presentedCrewIds.includes(navlog.crewId))) {
      if (pseudoCrewIds.includes(navlog.crewId)) {
        continue
      }
      const person = getCrewMember(this.crew, navlog.crewId)
      pseudoCrewIds.push(navlog.crewId)
      if (!person) {
        continue
      }
      this.crewActivity.push({
        id: navlog.crewId,
        forVersion: navlog.version,
        activityDate: {
          time: moment(navlog.syncDate).format('HH:mm'),
          date: moment(navlog.syncDate).format('DD.MM.YY'),
          diffInDays: getDiffInDays(navlog.syncDate, moment())
        },
        type: 'pseudo',
        isOldVersion: false,

        duty: getLocalizedName(person.duty),
        name: {
          formatted: getFormattedName(person),
          fullName: getFullName(person)
        },
        phone: person.contacts?.phone || [],
        navlogItem: navlog
      })
    }
  }

  recalcHistoryFields () {
    this.historyRoute = getFilteredHistory(this, 'depPort', 'arrPort')
    this.historyTailNumber = getFilteredHistory(this, 'tail')
    this.historyFlightNumber = getFilteredHistory(this, 'flightNum')
    this.historyEtd = getFilteredHistory(this, 'std', 'etd')
    this.historyCrew = getFilteredHistory(this, 'crew')
    this.anyChangesRoute = checkChanges(this, 'historyRoute')
    this.anyChangesTailNumber = checkChanges(this, 'historyTailNumber')
    this.anyChangesFlightNumber = checkChanges(this, 'historyFlightNumber')
    this.anyChangesEtd = checkChanges(this, 'historyEtd')
    this.anyChangesCrew = checkChanges(this, 'historyCrew')
  }

  getIataCode (icaoCode) {
    return this.#airports[icaoCode]?.iata.en || this.#airports[icaoCode]?.iata.ru
  }

  cloneFromVersion ({
    version,
    flightHistory,
    prevBriefing,
    referenceDate,
    crewActivity,
    initialCancelled,
    effectivePicDecisions
  }) {
    const briefing = Object.assign(new Briefing(), {
      currentVersion: version,
      hasFilesInSections: Object.values(version.sections).some(item => {
        return item.files.filesNum > 0 && store.state.sectionTypes.availableCodes.includes(item.code)
      }),
      status: version.status,
      versionNum: version.version,
      cancelled: prevBriefing ? prevBriefing.cancelled : initialCancelled,
      createDate: {
        displayTime: moment(version.createDate).format('HH:mm'),
        displayDate: moment(version.createDate).format('DD.MM.YY'),
        displayDateDiff: getDiffInDays(version.createDate, referenceDate)
      },
      etd: this.etd,
      displayTimeEtd: this.displayTimeEtd,
      displayDateEtd: moment(this.etd).format('DD.MM.YY'),
      std: this.std,
      displayTimeStd: this.displayTimeStd,
      displayDateStd: moment(this.std).format('DD.MM.YY'),
      flightNumber: this.flightNumber,
      isApprovedAndChanged: version.status === 'CH' && !!version.approveDate,
      approveDate: version.approveDate ? moment(version.approveDate).format('HH:mm') : '',
      displayApproveDate: formatedDate(version.approveDate, 'HH:mm, DD.MM.YY'),
      approveDateDiff: getDiffInDays(version.approveDate, referenceDate),
      tailNumber: this.tailNumber,
      depPort: this.depPort,
      depPortIata: this.depPortIata,
      arrPort: this.arrPort,
      arrPortIata: this.arrPortIata,
      crew: cloneDeep(this.crew),
      crewSize: this.crewSize,
      crewActivity: crewActivity.filter(item => item.forVersion === version.version),
      effectivePicDecisions,
      navlogItems: version.navlogItems || [],
      actuality: this.actuality,
      numOfPilots: this.numOfPilots,
      numOfStewards: this.numOfStewards,
      isCrewAvailable: false,
      changedSectionsCodes: [],
      changedFlightHistoryFields: []
    })

    if (version.approveUserId) {
      briefing.currentVersion.fdApprover = store.getters.userById(version.approveUserId)
    }

    if (['AP', 'AC', 'PA', 'CA'].includes(version.status)) {
      briefing.approveDate = moment(version.approveDate).format('HH:mm')
      briefing.displayApproveDate = moment(version.approveDate).format('HH:mm, DD.MM.YY')
      briefing.approveDateDiff = getDiffInDays(version.approveDate, referenceDate)
    }

    const history = (flightHistory || []).filter(item => {
      return item.fromVersion === version.version + 1
    })

    history.forEach(item => {
      switch (item.field) {
        case 'cancelled':
          briefing.cancelled = item.oldValue === 'true'
          break
        case 'depPort':
          briefing.depPort = item.oldValue
          briefing.depPortIata = briefing.getIataCode(item.oldValue)
          break
        case 'arrPort':
          briefing.arrPort = item.oldValue
          briefing.arrPortIata = briefing.getIataCode(item.oldValue)
          break
        case 'tail':
          briefing.tailNumber = item.oldValue
          break
        case 'flightNum':
          briefing.flightNumber = item.oldValue
          break
        case 'crew':
          briefing.crew = JSON.parse(item.oldValue)
          briefing.numOfPilots = briefing.crew.PILOT?.length || 0
          briefing.numOfStewards = briefing.crew.STEWARD?.length || 0
          briefing.crewSize = getCrewSize(briefing.crew)
          break
        case 'etd':
          briefing.etd = item.oldValue
          briefing.displayTimeEtd = moment(item.oldValue).format('HH:mm')
          briefing.displayDateEtd = moment(item.oldValue).format('DD.MM.YY')
          break
        case 'std':
          briefing.std = item.oldValue
          briefing.displayTimeStd = moment(item.oldValue).format('HH:mm')
          briefing.displayDateStd = moment(item.oldValue).format('DD.MM.YY')
          break
      }
    })

    if (briefing.numOfPilots || briefing.numOfStewards) {
      briefing.isCrewAvailable = true
    }

    briefing.navlogItems.forEach(item =>
        item.status = item.processingResult
            ? (item.processingResult === 'OK' ? 'OK' : 'ERROR')
            : 'WAIT')

    if (briefing.crewActivity.length) {
      briefing.prepareCrewActivityData()
    }

    const fieldNames = ['depPort', 'arrPort', 'tail', 'flightNum', 'crew', 'etd', 'std']

    flightHistory.forEach(item => {
      if (item.fromVersion === version.version && fieldNames.includes(item.field)) {
        if (!isEqual(item.oldValue, item.newValue)) {
          switch (item.field) {
            case 'depPort':
            case 'arrPort':
              briefing.changedFlightHistoryFields.push('route')
              break
            default:
              briefing.changedFlightHistoryFields.push(item.field)
              break
          }
        }
      }
    })

    if (prevBriefing !== null) {
      const prevSections = prevBriefing.currentVersion.sections
      const currSections = briefing.currentVersion.sections

      for (const code in prevSections) {
        if (currSections[code]?.version !== prevSections[code].version) {
          prevBriefing.changedSectionsCodes.push(code)
        }
      }
    }

    if (version.version === 1) {
      for (const code in version.sections) {
        if (version.sections[code].files.filesNum > 0 || version.sections[code].status === 'UN') {
          briefing.changedSectionsCodes.push(code)
        }
      }
    }

    return briefing
  }
}
